import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Calendar, MapPin, Building2, Wrench, ShieldCheck } from 'lucide-react';

// Replace with your local image path, e.g.:
// import IndustryImg from '../../Media/industry-experience.jpg';
import IndustryImg from '../../Media/Industry Experience.jpg';
import Mahindra from '../../Media/mahindra.webp';
import HEC from '../../Media/HEClogo.png';

const experiences = [
  {
    role: "Assistant Manager – Research & Design Department",
    company: "Mahindra & Mahindra Ltd.",
    location: "Nashik, Maharashtra, India",
    period: "2006 – 2009",
    type: "Automotive Industry",
    summary:
      "Spearheaded vehicle R&D and engineering initiatives, bridging theoretical system dynamics with large-scale shop-floor manufacturing and implementation.",
    points: [
      "Core contributions to automotive design cycles and validation workflows.",
      "Hands-on cross-functional execution in vehicle testing and systems integration.",
      "Grounded foundational research insights on implementation hurdles in large-scale operations."
    ],
    highlight: true
  },
  {
    role: "Student Intern – Foundry Forge Plant",
    company: "Heavy Engineering Corporation Ltd. (HEC)",
    location: "Ranchi, Jharkhand, India",
    period: "2004",
    type: "Heavy Manufacturing",
    summary:
      "Trained in heavy forging, industrial casting, and metallurgic operations across one of India's foundational manufacturing complexes.",
    points: [
      "Observed heavy equipment manufacturing and structural forging processes.",
      "Gained foundational exposure to plant management and industrial workflows."
    ],
    highlight: false
  }
];

export default function IndustryExperience() {
  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-4 mb-5">
        <div className="col-12 col-lg-7" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <Briefcase size={15} className="text-cyan" />
            <span className="small text-light fw-medium">Professional Practice & Background</span>
          </div>
          
          <h1 className="display-5 fw-bold text-white mb-3">
            Industry <span className="text-gradient">Experience</span>
          </h1>
          
          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Before entering academia full-time, years in core industrial engineering grounded 
            my strategic frameworks in shop-floor realities—proving that almost every failure 
            of strategic vision is rooted in execution.
          </p>

          <div className="d-flex flex-wrap gap-3">
            <div className="p-3 rounded-3 glass-card-sm text-center">
              <span className="fs-5 fw-bold text-cyan">3.5+</span>
              <span className="small text-secondary d-block">Years in Core R&D</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center">
              <span className="fs-5 fw-bold text-warning">Automotive</span>
              <span className="small text-secondary d-block">Design & Validation</span>
            </div>
          </div>
        </div>

        {/* Right Feature Graphic */}
        <div className="col-12 col-lg-5" data-aos="fade-left">
          <div className="position-relative p-2 rounded-4 glass-panel border-glow">
            <img
              src={IndustryImg}
              alt="Industry & Automotive Engineering Experience"
              className="img-fluid rounded-4 shadow-lg w-100"
              style={{ maxHeight: '280px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>

      {/* 2. Structured Experience Cards */}
      <div className="row g-4">
        {experiences.map((item, idx) => (
          <div className="col-12" key={idx} data-aos="fade-up" data-aos-delay={idx * 100}>
            <motion.div
              whileHover={{ y: -3 }}
              className="p-4 p-md-5 rounded-4 glass-panel position-relative overflow-hidden"
              style={{
                borderLeft: item.highlight
                  ? '4px solid #00f2fe'
                  : '4px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                  <span className={`badge px-3 py-1 rounded-pill small mb-2 ${
                    item.highlight
                      ? 'bg-info bg-opacity-10 text-cyan border border-info border-opacity-25'
                      : 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
                  }`}>
                    {item.type}
                  </span>
                  <h3 className="fw-bold text-white fs-4 mb-1">{item.role}</h3>
                  <div className="d-flex flex-wrap align-items-center gap-3 text-secondary small">
                    <span className="d-inline-flex align-items-center gap-1 text-light fw-medium">
                      <Building2 size={15} className="text-cyan" /> {item.company}
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                      <MapPin size={15} /> {item.location}
                    </span>
                  </div>
                </div>

                <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-dark border border-secondary border-opacity-25 text-warning small fw-semibold">
                  <Calendar size={14} /> {item.period}
                </div>
              </div>

              <p className="text-light opacity-90 small lh-lg mb-3">
                {item.summary}
              </p>

              <div className="border-top border-secondary border-opacity-25 pt-3">
                <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                  {item.points.map((pt, pIdx) => (
                    <li key={pIdx} className="d-flex align-items-start gap-2 text-secondary small">
                      <Wrench size={14} className="text-cyan mt-1 flex-shrink-0" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        ))}

      </div>
      <div className='row'>
        <marquee 
          behavior="scroll" 
          direction="left" 
          scrollamount="6" 
          scrolldelay="85" 
          loop="infinite"
          onMouseOver={(e) => e.currentTarget.stop()} 
          onMouseOut={(e) => e.currentTarget.start()}
          style={{ width: '100%', overflow: 'hidden' }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3rem' }}>
            <div 
              className="p-3 rounded-3" 
              style={{ 
                height: '90px', 
                width: '220px', 
                background: 'white', 
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img 
                src={Mahindra} 
                alt="Mahindra & Mahindra Ltd." 
                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
              />
            </div>

            <div 
              className="p-3 rounded-3" 
              style={{ 
                height: '90px', 
                width: '220px', 
                background: 'white', 
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img 
                src={HEC} 
                alt="Heavy Engineering Corporation Ltd. (HEC)" 
                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
              />
            </div>
          </div>
        </marquee>
      </div>

    </div>
  );
}